import gql from 'graphql-tag';

import { Query, Mutation } from 'core/index';

import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { CONTENT_FIELDS } from 'apps/content/gql';

export const COLLECTION_LIST_FIELDS = gql`
  fragment collectionListFields on Collection {
    id
    slug
    name
    description
    projectsCount
    images {
      id
      path
      url
    }
    content {
      id
      followersCount
      whitelabel
    }
    creatorProfile {
      id
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

export const COLLECTION_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment collectionFields on Collection {
    id
    canUpdate
    canDelete
    slug
    name
    description
    projectsCount
    images {
      id
      path
      url
    }
    content {
      ...contentFields
    }
    creatorProfile {
      id
      username
      fullName
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

export const COLLECTION_CONNECTION_FIELDS = gql`
  ${COLLECTION_LIST_FIELDS}
  fragment collectionConnectionFields on CollectionConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...collectionListFields
      }
    }
  }
`;

export const COLLECTION_MUTATION = gql`
  ${COLLECTION_FIELDS}
  mutation Collection($collectionInput: CollectionInput) {
    collection(collectionData: $collectionInput) {
      collection {
        ...collectionFields
      }
      project {
        id
        collections {
          id
        }
        starCount
        isStarred
      }
    }
  }
`;

export class CollectionMutation extends Mutation {
  static conf = {
    GQL: COLLECTION_MUTATION,
    inputName: 'collectionInput',
    resultPath: 'profile.collection',
  };
}

export const COLLECTION = gql`
  ${COLLECTION_FIELDS}
  ${PROJECT_CONNECTION_FIELDS}
  query Collection(
    $space: String
    $slug: String
    $cursor: String
    $count: Int
    $sortBy: String
  ) {
    collection(space: $space, slug: $slug) {
      ...collectionFields
      projects(first: $count, after: $cursor, sortBy: $sortBy) {
        ...projectConnectionFields
      }
    }
  }
`;

export class CollectionQuery extends Query {
  static conf = {
    GQL: COLLECTION,
    varNames: ['space', 'slug'],
    resultPath: 'collection',
  };
}

export const PROFILE_COLLECTIONS = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  query ProfileCollections(
    $username: String!
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    profile(username: $username) {
      id
      collections(first: $batchSize, after: $cursor, sortBy: $sortBy) {
        ...collectionConnectionFields
      }
    }
  }
`;

export class ProfileCollectionsQuery extends Query {
  static conf = {
    GQL: PROFILE_COLLECTIONS,
    varNames: ['username', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'profile.collections',
  };
}

export const INITIATIVE_COLLECTIONS = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  query InitiativeCollections(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    initiative(slug: $slug) {
      id
      collections(first: $batchSize, after: $cursor, sortBy: $sortBy) {
        ...collectionConnectionFields
      }
    }
  }
`;

export class InitiativeCollectionsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_COLLECTIONS,
    varNames: ['slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'initiative.collections',
  };
}

export const COLLECTION_PROJECTS = gql`
  ${PROJECT_CONNECTION_FIELDS}
  query CollectionProjects(
    $space: String
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    collection(space: $space, slug: $slug) {
      id
      projects(first: $batchSize, after: $cursor, sortBy: $sortBy) {
        ...projectConnectionFields
      }
    }
  }
`;

export class CollectionProjectsQuery extends Query {
  static conf = {
    GQL: COLLECTION_PROJECTS,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'collection.projects',
  };
}

const COLLECTIONS = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  query Collections($batchSize: Int, $cursor: String, $sortBy: String) {
    collections(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      ...collectionConnectionFields
    }
  }
`;

export class CollectionsQuery extends Query {
  static conf = {
    GQL: COLLECTIONS,
    varNames: ['batchSize', 'cursor', 'sortBy'],
  };
}
